import * as React from 'react';

import { TeamAccessType } from '@showbie-socrative/socrative-utils';
import { AxiosError } from 'axios';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { sendTeacherIntercomEvent } from '~analytics/intercom';
import { EventTypes } from '~analytics/types';
import { translate } from '~l10n/client';
import { UIButton } from '~shared/components/buttons';
import { DialogConfirmation } from '~shared/components/Dialog';
import { toast } from '~shared/components/Toast';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { ChangeToPaidAddSeatsDialog } from '~teacher/components/Account/TeamMembers/TeamMembersTable/AddSeatsDialog/ChangeToPaidAddSeatsDialog';
import { selectedPublicIdsAtom } from '~teacher/components/Account/TeamMembers/TeamMembersTable/atoms';
import { useChangeTeamAccessLevel } from '~teacher/hook/team/useChangeTeamAccessLevel';

export const isUpgradeMemberDialogOpenAtom = atom<boolean>(false);

export function UpgradeMemberDialog() {
  const selectedTeamId = useAtomValue(selectedTeamIdAtom);
  const selectedPublicIds = useAtomValue(selectedPublicIdsAtom.valueAtom);
  const resetSelectedPublicIds = useSetAtom(selectedPublicIdsAtom.resetAtom);

  const [isUpgradeMemberDialogOpen, setIsUpgradeMemberDialogOpen] = useAtom(
    isUpgradeMemberDialogOpenAtom
  );

  const openChangeToPaidAddSeatsDialog =
    ChangeToPaidAddSeatsDialog.useOpenDialog();

  const [isLoading, setIsLoading] = React.useState(false);

  const onClose = () => {
    resetSelectedPublicIds();
    setIsUpgradeMemberDialogOpen(false);
    setIsLoading(false);
  };

  const changeAccessLevelMutation = useChangeTeamAccessLevel();

  const onUpgradeMember = () => {
    setIsLoading(true);
    const [memberPublicId] = selectedPublicIds;
    changeAccessLevelMutation.mutate(
      {
        teamId: selectedTeamId,
        memberPublicId: memberPublicId,
        newAccessType: TeamAccessType.Paid,
      },
      {
        onSuccess: () => {
          toast(translate('Done'));
          onClose();
          setIsLoading(false);

          sendTeacherAmplitudeEvent(EventTypes.TeamsChangeSeat, {
            team_id: selectedTeamId,
            new_seat: TeamAccessType.Paid,
            previous_seat: TeamAccessType.Free,
          });
          sendTeacherIntercomEvent(EventTypes.TeamsChangeSeat);
        },
        onError: (error: AxiosError) => {
          const errorCode = error.response?.data?.code;
          setIsLoading(false);
          setIsUpgradeMemberDialogOpen(false);
          if (errorCode === 'can_not_change_member_access') {
            openChangeToPaidAddSeatsDialog();
          }
        },
      }
    );
  };

  return (
    <DialogConfirmation
      open={isUpgradeMemberDialogOpen}
      onClose={onClose}
      id="upgrade-member-dialog"
      title={translate('Upgrade Member')}
      actions={[
        <UIButton key="cancel" isLoading={isLoading} onClick={onClose}>
          {translate('Cancel')}
        </UIButton>,
        <UIButton
          key="confirm"
          isLoading={isLoading}
          appearance="primary"
          onClick={onUpgradeMember}
        >
          {translate('Upgrade Member')}
        </UIButton>,
      ]}
    >
      {translate(
        'Upgrading gives this member a seat and access to the team’s plan.'
      )}
    </DialogConfirmation>
  );
}
